import { ProductInterface, SidePanelInterface, VariantInterface, AddonGroupInterface } from '../types';
import SidePanel from './SidePanel';
import {
	Box,
	CircularProgress,
	FormGroup,
	Divider,
	FormControlLabel,
	Typography,
	Checkbox,
	ImageList,
	ImageListItem,
	styled,
	RadioGroup,
	Radio,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { CreateButton, InputField, Label, ModalTitle, TextArea } from './SharedComponents';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import ProductImageUpload from './ProductImageUpload';
import VariantEditor from './VariantEditor';
import AddonGroupsDisplay from './AddonGroupsDisplay';
import AddonGroupEditModal from './AddonGroupEditModal';
import { useSelector } from 'react-redux';
import { getCurrency } from '../services/tableHelper';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { ArrowLeftIcon } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationModal from './ConfirmationModal';

interface ProductModalProp extends Omit<SidePanelInterface, 'children'> {
	product?: ProductInterface | null;
	isNewProduct?: boolean;
	onProductUpdate?: (product: ProductInterface) => void;
	onProductDelete?: (productId: string) => void;
}

const fieldRowStyle = {
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'center',
	'@media (max-width: 768px)': {
		flexDirection: 'column',
		alignItems: 'flex-start',
	},
};

const ImageContainer = styled(Box)(({ theme }) => ({
	position: 'relative',
	height: '100%',
	'&:hover .deleteButton': {
		opacity: 1,
	},
}));

export default function ProductModal(props: ProductModalProp) {
	const {
		product,
		handleHidePanel,
		isNewProduct = false,
		onProductUpdate,
		onProductDelete,
		...sidePanelProps
	} = props;
	const [mode, setMode] = useState<'view' | 'edit'>(isNewProduct ? 'edit' : 'view');

	const [productName, setProductName] = useState<string>('');
	const [productDescription, setProductDescription] = useState<string>('');
	const [productPrice, setProductPrice] = useState<number>(0);
	const [minimumStockLevel, setMinimumStockLevel] = useState<number>(0);
	const [productImages, setProductImages] = useState<string[]>([]);
	const [isVisibleInStorefront, setIsVisibleInStorefront] = useState<boolean>(true);
	const [optionsType, setOptionsType] = useState<'variant' | 'addon'>('variant');
	const [variants, setVariants] = useState<VariantInterface[]>([]);
	const [addonGroups, setAddonGroups] = useState<AddonGroupInterface[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [isAddonGroupModalOpen, setIsAddonGroupModalOpen] = useState<boolean>(false);
	const [selectedAddonGroup, setSelectedAddonGroup] = useState<AddonGroupInterface | null>(null);
	const [isNewAddonGroup, setIsNewAddonGroup] = useState<boolean>(false);
	const isManagingVariantsLocally = useRef<boolean>(false);
	const isManagingAddonsLocally = useRef<boolean>(false);

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	useEffect(() => {
		// Reset mode when modal opens/changes product
		setMode(isNewProduct ? 'edit' : 'view');

		if (product) {
			let valueFormatted = Number(Number(product.default_price).toFixed(2));
			console.log('value formatted', valueFormatted, product.default_price);

			setProductName(product.name);
			setProductDescription(product.description);
			setProductPrice(valueFormatted);
			setMinimumStockLevel(product.min_stock_level);

			// Set visibility in storefront (default to true if not specified)
			setIsVisibleInStorefront(product.is_visible_in_storefront !== false);

			// Set options type (default to 'variant' if not specified)
			setOptionsType(product.options_type || 'variant');

			// Set variants if they exist (only if not managing locally)
			if (!isManagingVariantsLocally.current) {
				if (product.variants && Array.isArray(product.variants)) {
					setVariants(product.variants);
				} else {
					setVariants([]);
				}
			}

			// Set addon groups if they exist (only if not managing locally)
			if (!isManagingAddonsLocally.current) {
				if (product.addon_groups && Array.isArray(product.addon_groups)) {
					setAddonGroups(product.addon_groups);
				} else {
					setAddonGroups([]);
				}
			}

			// Parse product images if they exist
			console.log('ProductModal - Raw product_images:', product.product_images);

			if (product.product_images) {
				if (typeof product.product_images === 'string') {
					try {
						// If it's a JSON string, parse it
						console.log('ProductModal - Parsing product_images string:', product.product_images);
						const parsedImages = JSON.parse(product.product_images as string);
						console.log('ProductModal - Parsed images:', parsedImages);
						setProductImages(Array.isArray(parsedImages) ? parsedImages : []);
					} catch (e) {
						console.error('Error parsing product images:', e);
						setProductImages([]);
					}
				} else if (Array.isArray(product.product_images)) {
					// If it's already an array, use it directly
					console.log('ProductModal - Using product_images array directly:', product.product_images);
					setProductImages(product.product_images);
				}
			} else {
				console.log('ProductModal - No product_images found');
				setProductImages([]);
			}

			console.log('ProductModal - Final productImages state:', productImages);
		} else {
			setProductName('');
			setProductDescription('');
			setProductPrice(0);
			setMinimumStockLevel(0);
			setProductImages([]);
			setOptionsType('variant');
			setVariants([]);
			setAddonGroups([]);
			setIsVisibleInStorefront(true); // Default to true for new products
		}
	}, [product, isNewProduct]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		if (name === 'name') {
			setProductName(value);
		} else if (name === 'description') {
			setProductDescription(value);
		} else if (name === 'default_price') {
			let valueFormatted = Number(Number(value).toFixed(2));
			console.log('value formatted', valueFormatted, value);
			setProductPrice(valueFormatted);
		} else if (name === 'min_stock_level') {
			setMinimumStockLevel(Number(value));
		}
	};

	const hidePanel = (product: ProductInterface | null = null) => {
		isManagingVariantsLocally.current = false;
		isManagingAddonsLocally.current = false;
		setProductName('');
		setProductDescription('');
		setProductPrice(0);
		setMinimumStockLevel(0);
		setProductImages([]);
		setOptionsType('variant');
		setVariants([]);
		setAddonGroups([]);
		setIsVisibleInStorefront(true);
		// Reset mode to view to prepare for next opening
		setMode(isNewProduct ? 'edit' : 'view');
		if (product) {
			handleHidePanel(product);
		} else {
			handleHidePanel();
		}
	};

	const createUpdateProduct = (product: ProductInterface, productID: string | null = null) => {
		let method = productID ? 'PUT' : 'POST';
		let url = `/products/${productID ? productID : ''}`;

		if (method === 'PUT') {
			return apiClient.update(url, product);
		}

		return apiClient.post(url, product);
	};

	const isFieldsValid = () => {
		if (productName.length === 0) {
			notify('error', 'Name is required');
			return false;
		}
		if (productPrice === 0) {
			notify('error', 'Price is required');
			return false;
		}
		return true;
	};

	const handleDelete = () => {
		if (!product?.id) return;

		setIsDeleting(true);
		apiClient
			.delete(`/products/${product.id}`, true)
			.then(() => {
				setIsDeleting(false);
				setIsDeleteDialogOpen(false);
				notify('success', 'Product deleted successfully');

				// Remove product from the list
				if (onProductDelete && product.id) {
					onProductDelete(product.id);
				}

				hidePanel(); // Close modal after successful deletion
			})
			.catch((error: any) => {
				setIsDeleting(false);
				setIsDeleteDialogOpen(false);

				// Handle specific error messages
				if (error.response?.status === 403) {
					notify('error', 'Only shop owners can delete products');
				} else {
					notify('error', error.response?.data?.message || 'Failed to delete product');
				}
			});
	};

	const handleVariantsChange = (updatedVariants: VariantInterface[]) => {
		isManagingVariantsLocally.current = true;
		setVariants(updatedVariants);
		// Update parent component immediately when variants change
		if (product && onProductUpdate) {
			const updatedProduct = {
				...product,
				variants: updatedVariants,
			};
			onProductUpdate(updatedProduct);
		}
	};

	const handleAddonGroupsChange = (updatedAddonGroups: AddonGroupInterface[]) => {
		isManagingAddonsLocally.current = true;
		setAddonGroups(updatedAddonGroups);
		// Update parent component immediately when addon groups change
		if (product && onProductUpdate) {
			const updatedProduct = {
				...product,
				addon_groups: updatedAddonGroups,
			};
			onProductUpdate(updatedProduct);
		}
	};

	const handleAddAddonGroup = () => {
		setSelectedAddonGroup(null);
		setIsNewAddonGroup(true);
		setIsAddonGroupModalOpen(true);
	};

	const handleEditAddonGroup = (group: AddonGroupInterface) => {
		setSelectedAddonGroup(group);
		setIsNewAddonGroup(false);
		setIsAddonGroupModalOpen(true);
	};

	const handleSaveAddonGroup = (updatedGroup: AddonGroupInterface) => {
		if (isNewAddonGroup) {
			// Add new group
			handleAddonGroupsChange([...addonGroups, updatedGroup]);
		} else {
			// Update existing group
			const updatedGroups = addonGroups.map((g) => (g.id === updatedGroup.id ? updatedGroup : g));
			handleAddonGroupsChange(updatedGroups);
		}
	};

	const handleDeleteAddonGroup = (groupId: number) => {
		const updatedGroups = addonGroups.filter((g) => g.id !== groupId);
		handleAddonGroupsChange(updatedGroups);
	};

	const handleSave = () => {
		if (!isFieldsValid()) return;

		setIsLoading(true);
		let productID = product ? product.id : null;

		// Create the product JSON to send to the API
		console.log('ProductModal - Creating product with images:', productImages);

		// Create product JSON, setting product_images to null if it's an empty array
		const productJson: ProductInterface = {
			name: productName,
			description: productDescription,
			default_price: productPrice,
			min_stock_level: minimumStockLevel,
			product_images: productImages && productImages.length > 0 ? productImages : null,
			is_visible_in_storefront: isVisibleInStorefront,
			options_type: optionsType,
		};

		// Add addon groups if options_type is 'addon'
		if (optionsType === 'addon' && addonGroups.length > 0) {
			productJson.addon_groups = addonGroups;
		}

		console.log('ProductModal - Final productJson:', productJson);

		createUpdateProduct(productJson, productID)
			.then((result: any) => {
				console.log('updated', result);
				setIsLoading(false);

				// Process the returned product to ensure proper formatting
				const updatedProduct = { ...result.data };

				// Ensure product_images are properly formatted
				if (updatedProduct.product_images === null || updatedProduct.product_images === undefined) {
					updatedProduct.product_images = [];
				} else if (typeof updatedProduct.product_images === 'string') {
					try {
						if (updatedProduct.product_images === '[]' || updatedProduct.product_images === '') {
							updatedProduct.product_images = [];
						} else {
							updatedProduct.product_images = JSON.parse(updatedProduct.product_images);
						}
					} catch (e) {
						console.error('Error parsing product images:', e);
						updatedProduct.product_images = [];
					}
				} else if (Array.isArray(updatedProduct.product_images) && updatedProduct.product_images.length === 0) {
					updatedProduct.product_images = [];
				}

				// Ensure is_visible_in_storefront is properly formatted
				if (updatedProduct.is_visible_in_storefront === 0) {
					updatedProduct.is_visible_in_storefront = false;
				} else if (updatedProduct.is_visible_in_storefront === 1) {
					updatedProduct.is_visible_in_storefront = true;
				} else if (
					updatedProduct.is_visible_in_storefront === null ||
					updatedProduct.is_visible_in_storefront === undefined
				) {
					updatedProduct.is_visible_in_storefront = true;
				}

				hidePanel(updatedProduct);
			})
			.catch((error: any) => {
				setIsLoading(false);

				// Handle specific error codes
				if (error.response?.status === 403) {
					const errorData = error.response.data;
					if (errorData.code === 'PRODUCTS_LIMIT_EXCEEDED') {
						// The apiClient interceptor already shows the notification
						// Just ensure we don't crash the app
						console.log('Product limit exceeded:', errorData.message);
					} else {
						notify(
							'error',
							errorData.message || 'Forbidden: You do not have permission to perform this action'
						);
					}
				} else {
					// For other errors, the apiClient interceptor already shows the notification
					console.error('Error creating/updating product:', error);
				}
			});
	};

	const headerComponents = [];

	if (mode === 'view') {
		headerComponents.push(
			<CreateButton key="edit-button" onClick={() => setMode('edit')} outline>
				<ModeEditOutlinedIcon style={{ fontSize: '17px' }} />
				<span>Edit Product</span>
			</CreateButton>
		);
	}

	if (mode === 'edit' && !isNewProduct && product) {
		headerComponents.push(
			<CreateButton key="edit-button" onClick={() => setMode('view')} outline>
				<ArrowLeftIcon style={{ fontSize: '17px' }} />
				<span>Cancel Edit</span>
			</CreateButton>
		);
	}

	return (
		<SidePanel {...sidePanelProps} handleHidePanel={() => hidePanel()} headerComponents={headerComponents}>
			{mode === 'edit' && isNewProduct && <ModalTitle>Add Product</ModalTitle>}

			{mode === 'view' && product && (
				<>
					{/* View Mode */}
					{/* <Box sx={{ mb: 3 }}>
                        {productImages && productImages.length > 0 ? (
                            <ProductImageCarousel images={productImages} />
                        ) : (
                            <Box
                                sx={{
                                    height: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    mb: 2,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    No product images
                                </Typography>
                            </Box>
                        )}
                    </Box> */}

					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle2" color="text.secondary">
							Product Name
						</Typography>
						<Typography variant="h6">{productName}</Typography>
					</Box>

					<Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
						<Box sx={{ flex: 1 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Price
							</Typography>
							<Typography variant="body1">
								{currency} {productPrice}
							</Typography>
						</Box>

						<Box sx={{ flex: 1 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Visible in Storefront
							</Typography>
							<Typography variant="body1">{isVisibleInStorefront ? 'Yes' : 'No'}</Typography>
						</Box>
					</Box>

					{productDescription && (
						<Box sx={{ mb: 3 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Description
							</Typography>
							<Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
								{productDescription}
							</Typography>
						</Box>
					)}

					{/** Product Images Section */}
					{productImages && (
						<Box sx={{ mt: 2, mb: 4 }}>
							<ImageList
								sx={{ width: '100%', height: 'auto', marginTop: '5px', overflowY: 'initial' }}
								cols={4}
							>
								{productImages.map((imageUrl, index) => (
									<ImageListItem key={index}>
										<ImageContainer>
											<div
												style={{
													width: '100%',
													borderRadius: '8px',
													paddingTop: '100%', // Maintains a square aspect ratio
													backgroundImage: `url(${imageUrl})`,
													backgroundSize: 'cover', // Ensures the image covers the entire div
													backgroundPosition: 'center', // Centers the image
													backgroundRepeat: 'no-repeat', // Prevents the image from repeating
												}}
												aria-label={`Product ${index}`} // For accessibility
											></div>
										</ImageContainer>
									</ImageListItem>
								))}
							</ImageList>
						</Box>
					)}

					<Divider sx={{ my: 4 }} />

					{/* Options Type Display */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle2" color="text.secondary">
							Options Type
						</Typography>
						<Typography variant="body1">
							{product?.options_type === 'addon' ? 'Add-ons' : 'Variants'}
						</Typography>
					</Box>

					{/* Variants section */}
					{product?.options_type !== 'addon' && product?.variants && product.variants.length > 0 && (
						<Box sx={{ mb: 3 }}>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
								Variants
							</Typography>
							<Box sx={{ display: 'grid', gridGap: '8px' }}>
								{product.variants.map((variant: VariantInterface) => (
									<Box
										key={variant.id}
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											p: 1.5,
											borderRadius: 1,
											border: '1px solid',
											borderColor: variant.is_default_variant ? 'primary.main' : 'divider',
											bgcolor: variant.is_default_variant ? 'primary.50' : 'background.paper',
										}}
									>
										<Box sx={{ flex: 1, pr: 2 }}>
											<Box sx={{ display: 'flex', alignItems: 'center' }}>
												<Typography variant="body1" sx={{ fontWeight: 500 }}>
													{variant.name}
													<div
														className={`px-2 py-1 rounded-md text-xs ${
															Boolean(variant.is_visible_in_storefront) !== false
																? 'bg-green-100 text-green-800'
																: 'bg-gray-100 text-gray-800'
														}`}
														style={{ display: 'inline', marginLeft: '8px' }}
													>
														{Boolean(variant.is_visible_in_storefront) !== false
															? 'Visible'
															: 'Hidden'}
													</div>
												</Typography>
											</Box>
											{variant.description && (
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{
														mt: 0.5,
														display: '-webkit-box',
														WebkitBoxOrient: 'vertical',
														WebkitLineClamp: 2,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{variant.description}
												</Typography>
											)}
										</Box>
										<Typography
											variant="body1"
											sx={{
												fontWeight: 600,
												color: '#3a5bd9',
												minWidth: '90px',
												textAlign: 'right',
											}}
										>
											{currency}
											{Number(variant.default_price).toFixed(2)}
										</Typography>
									</Box>
								))}
							</Box>
						</Box>
					)}

					{/* Addon Groups section */}
					{product?.options_type === 'addon' && product?.addon_groups && product.addon_groups.length > 0 && (
						<Box sx={{ mb: 3 }}>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
								Add-on Groups
							</Typography>
							<Box sx={{ display: 'grid', gridGap: '16px' }}>
								{product.addon_groups.map((group: any) => (
									<Box
										key={group.id}
										sx={{
											p: 2,
											borderRadius: 1,
											border: '1px solid',
											borderColor: 'divider',
											bgcolor: 'background.paper',
										}}
									>
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												mb: 1,
											}}
										>
											<Typography variant="body1" sx={{ fontWeight: 600 }}>
												{group.name}
											</Typography>
											<Box sx={{ display: 'flex', gap: 1 }}>
												{group.is_required && (
													<div className="px-2 py-1 rounded-md text-xs bg-red-100 text-red-800">
														Required
													</div>
												)}
												<div className="px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-800">
													Select {group.min_addons_select || 0}-{group.max_addons_select || 1}
												</div>
											</Box>
										</Box>
										{group.addons && group.addons.length > 0 && (
											<Box sx={{ ml: 2 }}>
												{group.addons.map((addon: any) => (
													<Box
														key={addon.id}
														sx={{
															display: 'flex',
															justifyContent: 'space-between',
															alignItems: 'center',
															py: 0.5,
														}}
													>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
															<Typography variant="body2">{addon.name}</Typography>
															{addon.is_multi && (
																<div className="px-1 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
																	Max: {addon.quantity}
																</div>
															)}
															{addon.is_out_of_stock && (
																<div className="px-1 py-0.5 rounded text-xs bg-red-100 text-red-600">
																	Out of Stock
																</div>
															)}
														</Box>
														<Typography
															variant="body2"
															sx={{ color: '#3a5bd9', fontWeight: 500 }}
														>
															+{currency}
															{Number(addon.price || 0).toFixed(2)}
														</Typography>
													</Box>
												))}
											</Box>
										)}
									</Box>
								))}
							</Box>
						</Box>
					)}
				</>
			)}

			{mode === 'edit' && (
				<>
					{/* Edit Mode */}
					<FormGroup sx={{ flex: 8 }} className="mb-3">
						<Label>Name</Label>
						<InputField type="text" name="name" value={productName} onChange={handleChange} />
					</FormGroup>
					<FormGroup className="mb-3">
						<Label>Description</Label>
						<TextArea
							name="description"
							value={productDescription}
							onChange={handleChange}
							style={{ marginBottom: '0px' }}
						/>
					</FormGroup>
					<Box sx={fieldRowStyle}>
						<FormGroup>
							<Label>Unit Price</Label>
							<div
								className="flex items-center currency-input-wrapper"
								style={{
									backgroundColor: '#F3F3F3',
									borderRadius: '8px',
									width: '190px',
									border: '1px solid #dad8d8',
								}}
							>
								<div
									style={{
										backgroundColor: '#bcbcbc',
										padding: '10px 10px 10px 15px',
										borderRadius: '8px 0px 0px 8px',
										color: '#6f6f6f',
									}}
								>
									{currency}
								</div>
								<InputField
									type="number"
									name="default_price"
									value={productPrice === 0 ? '' : productPrice} // Allow empty string when cleared
									onChange={(e) => {
										const value = e.target.value;
										setProductPrice(value === '' ? 0 : parseFloat(value)); // Handle empty string and convert to number
									}}
									min={0}
									style={{
										marginTop: '0px',
										paddingLeft: '10px',
										width: '130px',
										display: 'inline-block',
										border: 'none',
									}}
								/>
							</div>
						</FormGroup>

						<FormGroup style={{ paddingTop: '20px' }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={isVisibleInStorefront}
										onChange={(e) => setIsVisibleInStorefront(e.target.checked)}
										color="primary"
									/>
								}
								label="Show in storefront"
							/>
						</FormGroup>
					</Box>

					<Divider sx={{ my: 6 }} />

					{/* Product Image Upload component - only shown in edit mode */}
					<ProductImageUpload
						productId={product?.id}
						initialImages={productImages}
						onChange={(images) => {
							setProductImages(images);
							// If we're editing an existing product, immediately update the parent's state
							if (!isNewProduct && product?.id && onProductUpdate) {
								const updatedProduct = {
									...product,
									product_images: images,
								};
								onProductUpdate(updatedProduct);
							}
						}}
					/>

					<Divider sx={{ my: 2 }} />

					{/* Options Type Selector */}
					<Box sx={{ mb: 3 }}>
						<Label>Product Options Type</Label>
						<RadioGroup
							value={optionsType}
							onChange={(e) => setOptionsType(e.target.value as 'variant' | 'addon')}
							row
						>
							<FormControlLabel
								value="variant"
								control={<Radio />}
								label="Variants (Different versions of the product)"
								disabled={!isNewProduct && product?.options_type === 'addon'}
							/>
							<FormControlLabel
								value="addon"
								control={<Radio />}
								label="Add-ons (Optional extras)"
								disabled={!isNewProduct && product?.options_type === 'variant'}
							/>
						</RadioGroup>
						{!isNewProduct && (
							<Typography variant="caption" color="text.secondary">
								Options type cannot be changed after product creation
							</Typography>
						)}
					</Box>

					<Divider sx={{ my: 2 }} />

					{/* Conditionally show Variant Editor or Addon Groups Display */}
					{optionsType === 'variant' ? (
						<VariantEditor productId={product?.id} variants={variants} onChange={handleVariantsChange} />
					) : (
						<AddonGroupsDisplay
							addonGroups={addonGroups}
							onAddGroup={handleAddAddonGroup}
							onEditGroup={handleEditAddonGroup}
							currency={currency}
						/>
					)}

					<CreateButton onClick={handleSave} style={{ marginTop: '20px', width: '100%' }}>
						<CircularProgress
							style={{ visibility: isLoading ? 'visible' : 'hidden', position: 'absolute' }}
							size={15}
							color="inherit"
						/>
						<span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>Save Product</span>
					</CreateButton>

					{/* Delete Button - only show for existing products */}
					{product && !isNewProduct && (
						<Box sx={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
							<CreateButton
								onClick={() => setIsDeleteDialogOpen(true)}
								style={{ marginTop: '20px', width: '100%', borderColor: '#dc2626', color: '#dc2626' }}
								outline
							>
								<DeleteIcon style={{ fill: '#dc2626' }} />
								<span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>Delete Product</span>
							</CreateButton>
						</Box>
					)}
				</>
			)}

			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				isOpen={isDeleteDialogOpen}
				title="Delete Product"
				message={`Are you sure you want to delete "${productName}"? This action cannot be undone. The product and all its variants will be removed from your inventory.`}
				confirmText="Delete Product"
				cancelText="Cancel"
				onConfirm={handleDelete}
				onCancel={() => setIsDeleteDialogOpen(false)}
				isLoading={isDeleting}
				variant="danger"
			/>

			{/* Addon Group Edit Modal */}
			<AddonGroupEditModal
				isVisible={isAddonGroupModalOpen}
				onClose={() => setIsAddonGroupModalOpen(false)}
				addonGroup={selectedAddonGroup}
				onSave={handleSaveAddonGroup}
				onDelete={handleDeleteAddonGroup}
				currency={currency}
				isNew={isNewAddonGroup}
			/>
		</SidePanel>
	);
}
